import React from "react"
import styled from "styled-components"
import {BudgetCategories} from "@irati/types"
import tracking from "@irati-tracking/wallet/rewardPointsWallet"
import RewardPointsIcon from "@irati/ui/images/reward_point.svg"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import {Body} from "@irati/ui/src/Typography"
import FormattedMessage, {FormattedNumber} from "@irati/utils/lang"
import {BudgetAny, useBudgetsList} from "@irati/wallet/hooks/budget"
import ColorIcon from "../../Budgeting/ColorIcon"
import ShowMore from "./ShowMore"
import {WalletRoutes} from "@irati-web/wallet/src/enums/Routes"
import useAuth from "@irati/auth/helpers/auth/useAuth"

const CategoriesContainer = styled.ul`
  /* reset some bootstrap spacings */
  margin: 0;
  padding: 0;
  list-style-type: none;

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));

  row-gap: ${({theme}) => theme.spacing.two};
  column-gap: ${({theme}) => theme.spacing.four};
`

interface Props {
  /**
   * minHeight used by the `ShowMore` component, which will try to show everything if it can
   * You'll need to carfully adjust this number by eye if you change the size of the Category component
   */
  height: number
  budgets?: BudgetCategories
  hideCta?: boolean
}

export default function Categories({budgets, height, hideCta}: Props) {
  const {data} = useBudgetsList()

  if (!data) return null

  const unassigned = budgets?.unassigned ?? data.budgets.unassigned
  const categories = budgets?.categories ?? data.budgets.categories

  return (
    <ShowMore height={height} NonExpander={() => null}>
      <CategoriesContainer>
        <Category budget={unassigned} showCta={!hideCta} />
        {categories.map((c) => (
          <Category budget={c} key={c.uuid} showCta={!hideCta} />
        ))}
      </CategoriesContainer>
    </ShowMore>
  )
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: ${({theme}) => theme.spacing.half};

  /* first column */
  & > *:nth-child(2n - 1) {
    justify-self: end;
  }
`

const BodyEllipsis = styled(Body)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

interface ICategory {
  budget: BudgetAny
  showCta?: boolean
}
function Category({budget, showCta}: ICategory) {
  const shouldBuyMore = budget.amount === 0
  const auth = useAuth()
  const canTopUpRewardPoints = auth.productPermissions?.wallet?.flexiRewards?.canTopUpRewardPoints

  return (
    <li>
      <Grid>
        <ColorIcon color={budget.metadata.color} />
        <BodyEllipsis>{budget.name}</BodyEllipsis>

        <img alt="" src={RewardPointsIcon} width="14px" />
        <Body small weight="bold">
          <FormattedNumber value={budget.amount / 100} />
        </Body>
      </Grid>

      {showCta && (
        <ButtonRouterLink
          to={shouldBuyMore && canTopUpRewardPoints ? WalletRoutes.FLEXI_CHECKOUT + "/top-up" : WalletRoutes.FLEXI_ASSIGN + "/recipients"}
          cta
          small
          onClick={() => tracking.giveRewardPoints("rp_reward_wallet_budget")}>
          {shouldBuyMore && canTopUpRewardPoints ? (
            <FormattedMessage id="wallet.reward.budget.rewardWallet.cta.buyPoints" />
          ) : (
            <FormattedMessage id="wallet.reward.budget.rewardWallet.cta.givePoints" />
          )}
        </ButtonRouterLink>
      )}
    </li>
  )
}
